import React, { useEffect, useState } from "react";
import { Coffee, ForkKnife, UsersThree, Siren, Timer, Play } from "@phosphor-icons/react";
import { useBreak, fmtDur, toneOf, BREAK_TYPES } from "@/context/BreakContext";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const ICONS = { "Lunch Break": ForkKnife, "Team Break": UsersThree, "Short Break": Coffee, Emergency: Siren };

function useElapsed(startedAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  return startedAt ? (now - new Date(startedAt).getTime()) / 1000 : 0;
}

export function BreakMenu() {
  const { staff, active, start, busy } = useBreak();
  if (!staff || active) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:border-amber-400 hover:text-amber-600 transition-colors" data-testid="break-menu-btn" disabled={busy}>
          <Coffee size={17} weight="duotone" /><span className="hidden md:inline">Take a Break</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 bg-white">
        <DropdownMenuLabel className="text-xs text-slate-500">Report a break. Your screen will lock.</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {BREAK_TYPES.map((b) => {
          const Icon = ICONS[b.type];
          return (
            <DropdownMenuItem key={b.type} onSelect={() => start(b.type)} className="cursor-pointer py-2.5" data-testid={`break-start-${b.type.split(" ")[0].toLowerCase()}`}>
              <span className="w-8 h-8 rounded-lg grid place-items-center mr-2.5" style={{ background: `${b.tone}1a`, color: b.tone }}><Icon size={17} weight="fill" /></span>
              <span className="flex-1"><span className="block text-sm font-semibold text-slate-900">{b.type}</span><span className="block text-[11px] text-slate-400">{b.hint}</span></span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Ring({ pct, color }) {
  const r = 120, c = 2 * Math.PI * r;
  return (
    <svg width="280" height="280" viewBox="0 0 280 280" className="absolute inset-0 -rotate-90">
      <circle cx="140" cy="140" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
      <circle cx="140" cy="140" r={r} stroke={color} strokeWidth="10" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(1, pct))} style={{ transition: "stroke-dashoffset 1s linear" }} />
    </svg>
  );
}

export function BreakOverlay() {
  const { active, end, busy } = useBreak();
  const elapsed = useElapsed(active?.started_at);
  useEffect(() => {
    if (!active) return undefined;
    const block = (e) => { if (e.key === "Escape" || e.key === "Tab") e.preventDefault(); };
    window.addEventListener("keydown", block, true);
    return () => window.removeEventListener("keydown", block, true);
  }, [active]);
  if (!active) return null;
  const limit = active.limit_min ? active.limit_min * 60 : null;
  const over = limit && elapsed > limit;
  const color = over ? "#ef4444" : toneOf(active.type);
  const Icon = ICONS[active.type] || Timer;
  return (
    <div className="fixed inset-0 z-[200] grid place-items-center bg-slate-950/85 backdrop-blur-xl select-none" data-testid="break-overlay" role="dialog" aria-modal="true">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }} />
      <div className="relative text-center px-6">
        <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em]" style={{ color }}><span className="w-2 h-2 rounded-full animate-pulse" style={{ background: color }} />Screen locked · On break</p>
        <h2 className="font-heading font-extrabold text-white text-4xl sm:text-5xl mt-3 flex items-center justify-center gap-3" data-testid="break-overlay-type"><Icon size={40} weight="fill" style={{ color }} />{active.type}</h2>
        <div className="relative w-[280px] h-[280px] mx-auto my-8">
          <Ring pct={limit ? elapsed / limit : (elapsed % 3600) / 3600} color={color} />
          <div className="absolute inset-0 grid place-items-center">
            <div>
              <p className="font-mono font-bold text-white text-6xl tabular-nums" data-testid="break-overlay-timer">{fmtDur(elapsed)}</p>
              <p className={`text-sm mt-2 font-semibold ${over ? "text-red-400" : "text-white/50"}`} data-testid="break-overlay-limit">
                {limit ? (over ? `Over by ${fmtDur(elapsed - limit)}` : `${fmtDur(limit - elapsed)} left of ${active.limit_min} min`) : "No time limit"}
              </p>
            </div>
          </div>
        </div>
        <p className="text-white/40 text-xs mb-6">Started at {new Date(active.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · Your team can see you're on break</p>
        <button onClick={end} disabled={busy} style={{ background: "#ffffff", color: "#020617", boxShadow: `0 0 0 4px ${color}55, 0 12px 40px -8px ${color}` }} className="inline-flex items-center gap-2 h-14 px-10 rounded-full font-heading font-extrabold text-lg hover:scale-[1.03] active:scale-95 transition-transform disabled:opacity-60" data-testid="break-end-btn">
          <Play size={20} weight="fill" />{busy ? "Resuming…" : "End Break & Resume Work"}
        </button>
      </div>
    </div>
  );
}
